<template>
    <div class="custom-field custom-field-small">
        <template v-if="savedInFolder">
            <div class="share-from" v-if="spaceName || folderName">
                <ElPopover
                    :visible-arrow="false"
                    :open-delay="popperOpenDelay"
                    placement="right"
                    trigger="hover">
                    <p style="max-width: 526px; word-break: break-word; text-align: left;">
                        <span>{{ $t('customFields.currentFolderPath') }}</span>
                        <span>{{ spaceName }}</span>
                        <span> > </span>
                        <span>{{ folderName }}</span>
                    </p>
                    <div slot="reference" class="share-from-folder">
                        <div class="share-from-folder__value">
                            <img v-if="spaceIcon" :src="spaceIcon" alt="" class="workspace-icon">
                            <div class="workspace-name" :title="spaceName">
                                <span>{{ spaceName }}</span>
                            </div>
                            <div class="folder-name" :title="folderName">
                                <span>{{ folderName }}</span>
                            </div>
                        </div>
                    </div>
                </ElPopover>
                <div class="share-from-folder__family" v-if="!isFamilySearch && !loading && isFamilyFieldOpen">
                    {{ $t(`customFields.familyFieldType.${currentFolder.collapse_field}`) }}
                    <div class="family-status">
                        {{ $t('customFields.isFamilyFieldOpen') }}
                    </div>
                </div>
                <representativePatent :isRepresentative="isFamilyMainPatent" v-if="isFamilySearch && !loading" />
            </div>
            <div class="custom-field__list custom-field__list-small">
                <CustomFieldLabelPane
                    v-if="controller && activeTab==='customField'"
                    :controller="controller"
                />
            </div>
        </template>
        <p v-else class="unSaved">
            {{ $t('customFields.unsavedTip') }}
        </p>
    </div>
</template>

<script>
import { Popover } from 'element-ui';
import { get, filter } from 'lodash';

import { POPPER_OPEN_DELAY } from '../../constants';
import { CustomFieldLabelPane as PtCustomFieldLabelPane } from 'patsnap-biz';
import { Site, SLD } from '@patsnap-utils/biz';
import CustomFieldController from './controller';
import { useFamilySearchPage } from '@/composables/use-family-search-page';
import { useFamilySearch } from '@/composables/use-family-search';
import { useFamilySearchUrlParams } from '@/composables/use-family-search-url-params';
import representativePatent from './components/representativePatent';

export default {
    name: 'FieldList',
    components: {
        ElPopover: Popover,
        CustomFieldLabelPane: PtCustomFieldLabelPane.CustomFieldLabelPane,
        representativePatent,
    },
    props: {
        patentId: {
            type: String,
            default: ''
        },
        activeTab: {
            type: String,
            default: ''
        },
        fieldList: {
            type: Array,
            default: () => {
                return [];
            }
        },
        pinnedFields: {
            type: Array,
            default: () => {
                return [];
            }
        },
        folderName: {
            type: String,
            default: ''
        },
        spaceName: {
            type: String,
            default: ''
        },
        spaceIcon: {
            type: String,
            default: ''
        },
        spaceType: {
            type: String,
            default: ''
        },
        spaceId: {
            type: String,
            default: ''
        },
        isOwner: {
            type: String,
            default: ''
        },
        folderId: {
            type: String,
            default: ''
        },
        currentFolder: {
            type: Object,
            default: null,
        },
        taskId: {
            type: String,
            required: false
        },
        customFieldsDisplay: {
            type: Boolean,
            default: false
        },
        canEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        savedInFolder: {
            type: Boolean,
            default: true,
        }
    },
    setup() {
        const { isFamilySearch } = useFamilySearch();
        const { deferred: familySearchPageDeferred, currentActiveItem } = useFamilySearchPage();
        const { patentId: curUrlPatentId } = useFamilySearchUrlParams();

        return {
            isFamilySearch,
            familySearchPageDeferred,
            currentActiveItem,
            curUrlPatentId,
        };
    },
    data () {
        return {
            controller: null,
            loading: true,
        };
    },
    computed: {
        selfFieldList () {
            return filter(this.fieldList, ({ shared, public_field }) => !shared && !public_field);
        },
        sharedFieldList () {
            return filter(this.fieldList, ({ shared }) => shared);
        },
        publicFieldList () {
            return filter(this.fieldList, ({ public_field }) => public_field);
        },
        isFamilyFieldOpen () {
            const isOpen = get(this, 'currentFolder.sync_field_value');
            const fieldPermission = get(this, 'canEdit');
            const collapseField = get(this, 'currentFolder.collapse_field');
            return fieldPermission && isOpen && ['FAMILY_ID', 'INPADOC_FAMILY_ID', 'EXTEND_FAMILY', 'APNO_FACET'].includes(collapseField);
        },
        // 同族模式下族代表patentId
        familyMainPatentId() {
            return this.currentActiveItem?.patent_id;
        },
        // 是否处于同族模式的主专利下
        isFamilyMainPatent() {
            return this.familyMainPatentId === this.curUrlPatentId;
        },
    },
    async created () {
        this.siteLang = this.$i18n.locale;
        this.popperOpenDelay = POPPER_OPEN_DELAY;
        if (this.isFamilySearch) {
            await this.familySearchPageDeferred.promise
        }
        this.loading = false;
        this.controller = new CustomFieldController({
            vm: this,
            workspaceProductDomain: new Site({ sld: SLD.WORKSPACE }).href,
            spaceType: this.spaceType,
            taskId: this.taskId,
            spaceId: this.spaceId,
            folderId: this.folderId,
            fields: this.fieldList,
            isOwner: this.isOwner,
            patentId: this.patentId,
            pinnedFields: this.pinnedFields,
            customFieldsDisplay: this.customFieldsDisplay,
            spaceScopeFieldTip: this.$t('snapBusiness.customField.spaceScopeFieldTipInAnalytics'),
            // 同族模式下专用参数
            familyMainPatentId: this.familyMainPatentId,
        });
    }
};
</script>

<style lang="scss">
.custom-field {
    .custom-field__list {
        overflow-y: auto;
        overflow-x: hidden;
        position: relative;
        height: 100%;

        &::after {
            content: '';
            display: block;
            width: 100%;
            height: 18px;
        }

        .custom-field-label-pane__render {
            .text-multi-lines-wrapper {
                pre {
                    margin: 0;
                }
            }
        }
    }

    .share-from {
        .el-popover__reference {
            cursor: pointer;
        }

        overflow: hidden;
        background: #ffffff;
        position: relative;
        line-height: 18px;
        flex-shrink: 0;

        .share-from-folder {
            display: flex;
            align-items: center;
            font-size: 12px;
            height: 24px;
            line-height: 18px;
            color: $text-hint;
            word-break: break-all;

            &__label {
                flex-shrink: 0;
            }

            &__value {
                flex: 1;
                width: 0;
                display: flex;
                align-items: center;
            }

            &__family {
                font-size: 12px;
                color: $neutral-blue-300;
                display: flex;
                line-height: 18px;
                margin-top: 8px;

                .family-status {
                    display: inline-block;
                    width: 48px;
                    height: 18px;
                    background: #45a321;
                    box-shadow: -1px 0px 2px 0px rgba(0, 0, 0, 0.06);
                    text-align: center;
                    font-weight: 500;
                    color: #ffffff;
                    letter-spacing: 1px;
                    margin-left: 4px;
                }
            }

            .workspace-icon {
                width: 16px;
                height: 16px;
                margin-right: 4px;
                border-radius: 4px;
            }

            .workspace-name,
            .folder-name {
                cursor: pointer;
                display: inline-block;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 47%;
                vertical-align: bottom;
            }

            .folder-name::before {
                content: ' > ';
            }
        }
    }

    .patsnap-ui-input__inner {
        border: 1px solid $neutral-blue-60 !important;
    }
}

.custom-field-small {
    height: 100%;
    display: flex;
    flex-direction: column;

    .share-from {
        padding: 8px 16px;
        box-shadow: -1px 0px 2px 0px rgba(0, 0, 0, 0.06), 0px 4px 10px 0px rgba(0, 0, 0, 0.06);
    }
}
.unSaved {
    width: 200px;
    margin: 120px auto;
    color: $text-primary;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
}
</style>
